import React, { useRef, useState } from "react";
import PropTypes from 'prop-types';
import { PatternFormat } from "react-number-format";
import { Alert, AlertTitle, InputAdornment, MenuItem } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import Input from "@/components/Tags/Input";
import CallIcon from '@mui/icons-material/Call';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LoadingButton from "@/components/LoadingButton";
import axios from "@/lib/axios";
import Button from "@/components/Tags/Form/Button";

const defaultValues = {
    name: '',
    mobile_no: '',
    email: '',
    message: '',
};

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
        <PatternFormat
            {...other}
            getInputRef={ref}
            onValueChange={(values, sourceInfo) => {
                if(values.value){
                    onChange(values.value);
                }
            }}
            format="### ### ####"
            allowEmptyFormatting mask="-"
        />
    );
});

NumberFormatCustom.propTypes = {
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

export default function ContactUs(){
    const { handleSubmit, reset, setValue, setError, clearErrors, getValues, control, trigger, formState: { errors, isValid } } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: defaultValues
     });

     const [loading, setLoading] = useState(false);
     const [alertStatus, setAlertStatus] = useState(null);
     const [alertMessage, setAlertMessage] = useState(null);

     const onSubmit = async (data) => {
        setLoading(true);
        await axios.get('/sanctum/csrf-cookie');
        await axios.post(process.env.API_URL+`/contact_us`, data)
                .then(function (response) {
                    setAlertStatus(response.data.status);
                    setAlertMessage(response.data.message);
                })
                .catch(function (error) {
                    setAlertStatus(203);
                    setAlertMessage('Connection Failed. Try again.');
                });
        setLoading(false);
    };

    const onError = (data) => {
        console.log('Invalid Form!');
    };
    return(
        <div className='max-w-screen-xl mx-auto bg-white'>
            <div className='relative grid sm:grid-cols-2 grid-cols-1 sm:px-6 px-3 pt-4 pb-8'>
                <div className='px-4 pt-4'>
                    <h1 className="text-2xl font-semibold tracking-wide text-rapide-black pb-4">Contact Us</h1>
                    {alertStatus && (
                        <Alert severity={`${(alertStatus  == 200) ? 'success' : 'error'}`}>
                            {alertMessage}
                        </Alert>
                    )}
                    <form className="mt-2" onSubmit={handleSubmit(onSubmit, onError)}>
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        type="text"
                                        id="name"
                                        name="name"
                                        label="Name"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        error={errors.name != null}
                                        helperText={errors.name && errors.name?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                    />
                                )
                            }}
                            name="name"
                            control={control}
                            rules={{
                                required: 'Please enter your name',
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        fullWidth
                                        type="text"
                                        id="mobile_no"
                                        name="mobile_no"
                                        label="Mobile Number"
                                        variant="filled"
                                        size="small"
                                        error={errors.mobile_no != null}
                                        helperText={errors.mobile_no && errors.mobile_no?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                            startAdornment: <InputAdornment position="start">+63</InputAdornment>,
                                            inputComponent: NumberFormatCustom,
                                        }}
                                    />
                                )
                            }}
                            name="mobile_no"
                            control={control}
                            rules={{
                                required: "Please enter your mobile number",
                                minLength: {
                                    value: 10,
                                    message: 'Invalid mobile number.'
                                },
                                maxLength: {
                                    value: 10,
                                    message: 'Invalid mobile number.'
                                },
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        fullWidth
                                        type="email"
                                        id="email"
                                        name="email"
                                        label="Email"
                                        variant="filled"
                                        size="small"
                                        error={errors.email != null}
                                        helperText={errors.email && errors.email?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                            startAdornment: <InputAdornment position="start"><EmailOutlinedIcon/></InputAdornment>,
                                        }}
                                    />
                                )
                            }}
                            name="email"
                            control={control}
                            rules={{
                                required: 'Please enter your email address',
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        type="text"
                                        id="message"
                                        name="message"
                                        label="Message"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        multiline
                                        rows={4}
                                        error={errors.message != null}
                                        helperText={errors.message && errors.message?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                    />
                                )
                            }}
                            name="message"
                            control={control}
                            rules={{
                                required: 'Please enter your message',
                            }}
                        />
                        <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
                            <div className="md:col-start-2 col-start-1">
                                <Button type="submit" loading={loading} label="Submit" llabel="Submitting..." />
                            </div>
                        </div>
                    </form>
                </div>
                <div className='px-4 pt-4'>
                    <div
                        className='w-full'
                        dangerouslySetInnerHTML={{
                            __html: `<iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3861.613456480296!2d121.00912601533375!3d14.564083681873951!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3397c90abb2b69db%3A0xb1e57cf5fbe7dcc8!2sRapid%C3%A9%201%20San%20Antonio%20Makati%20-%20Auto%20Service%2C%20Car%20Repair!5e0!3m2!1sen!2sph!4v1667381367326!5m2!1sen!2sph" width="100%" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>`,
                        }}></div>
                </div>
            </div>
            <div className="max-w-screen-lg mx-auto grid md:grid-cols-3 grid-cols-1 gap-4 pt-4 pb-14">
                <div className="flex flex-row">
                    <div>
                        <CallIcon fontSize="large" className="text-rapide-yellow w-14"/>
                    </div>
                    <div>
                        <h1 className="font-bold text-xl">Call Us</h1>
                        <a className="font-bold block" href="tel:+639660619979">0966 061 9979 (Globe)</a>
                        <a className="font-bold" href="tel:+639192694103">0919 269 4103 (Smart)</a>
                    </div>
                </div>
                <div className="flex flex-row">
                    <div>
                        <AccessTimeFilledIcon fontSize="large" className="text-rapide-yellow w-14"/>
                    </div>
                    <div>
                        <h1 className="font-bold text-xl">Office Hours</h1>
                        <p>Main Office: Monday to Sunday</p>
                        <p>8:30 AM - 5:30 PM</p>
                    </div>
                </div>
                <div className="flex flex-row">
                    <div>
                        <LocationOnIcon fontSize="large" className="text-rapide-yellow w-14"/>
                    </div>
                    <div>
                        <h1 className="font-bold text-xl">Location</h1>
                        <p>1166 Chino Roces Avenue, Corner Estrella Avenue, Makati</p>
                    </div>
                </div>
            </div>
        </div>
    )
}